import React, { ChangeEvent, forwardRef } from 'react';
import { formatToPhone } from '~/utils/formatPhoneNumber';

type MobilePhoneNumberInputProps =
  React.InputHTMLAttributes<HTMLInputElement> & {
    floatLabel: boolean;
    submitted?: boolean;
    useDropshadow?: boolean;
    isReturning?: boolean;
    borderColor?: string;
    responsive?: boolean;
    handleOnChange?: (val: string) => void;
  };

const INPUT_HEIGHT_PX = 56; // matches legacy styles

export const MobilePhoneNumberInput = forwardRef<
  HTMLInputElement,
  MobilePhoneNumberInputProps
>(function MobilePhoneNumberInput(
  {
    floatLabel,
    submitted,
    useDropshadow,
    isReturning, // kept for API compatibility (width auto-handled)
    borderColor = '#80d6b0',
    responsive,
    className,
    style,
    type = 'text',
    value,
    onChange,
    handleOnChange,
    ...rest
  },
  ref
) {
  // desktop width accounts for returning state like legacy styles
  const bgImage = submitted
    ? 'url(/images/green-checkmark-circle.svg)'
    : undefined;

  const desktopWidthClass = responsive
    ? 'desktop:w-full'
    : useDropshadow
    ? isReturning
      ? 'desktop:[width:calc(25.625rem+3.5rem)]' // 410px + 3.5rem
      : 'desktop:w-[25.625rem]' // 410px
    : isReturning
    ? 'desktop:[width:calc(19.5rem+3.5rem)]' // 312px + 3.5rem
    : 'desktop:w-[19.5rem]'; // 312px

  const widthClasses = responsive
    ? ['w-full', desktopWidthClass]
    : [
        // Base equals legacy default (312px)
        'w-[19.5rem]',
        // 321px–350px special case
        'min-[321px]:max-[350px]:w-[280px]',
        // 720+
        useDropshadow ? 'tablet:w-[410px]' : 'tablet:w-[280px]',
        // 1080+
        desktopWidthClass
      ];

  const classes = [
    // sizing/layout
    'box-border',
    'h-14', // 56px
    ...widthClasses,
    'flex-1',
    // visuals
    'text-left',
    'bg-no-repeat',
    'appearance-none',
    'outline-0',
    // legacy focus-visible outline
    'focus-visible:outline-2',
    'focus-visible:outline-[#BBF174]',
    'focus-visible:outline-offset-4',
    'rounded-[8px]',
    // placeholder styling parity
    'placeholder:text-[#1D252C]',
    'placeholder:text-[14px]',
    // spacing
    floatLabel ? 'pt-[28px] pb-[18px] pl-4 pr-[130px]' : 'p-4 pr-[130px]'
  ];

  if (useDropshadow) {
    classes.push(
      'shadow-[6px_6px_0_0_#1D252C]',
      'border',
      'border-[#1D252C]',
      'rounded-[8px]'
    );
  } else {
    classes.push('border');
  }

  // widen on desktop when dropshadow to match legacy widths
  if (!responsive) {
    classes.push(useDropshadow ? 'desktop:w-[410px]' : 'desktop:w-[312px]');
  }

  // disabled background color parity: submitted uses emailInputBackground (#EEFBF5), otherwise white
  classes.push(submitted ? 'disabled:bg-[#EEFBF5]' : 'disabled:bg-white');

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    let formatted = raw;
    switch (type) {
      case 'tel':
        formatted = formatToPhone(raw) || '';
        break;
      default:
        formatted = raw;
    }
    handleOnChange?.(formatted);
    onChange?.(e);
  };

  return (
    <input
      ref={ref}
      {...rest}
      type={type}
      value={value}
      onChange={handleChange}
      maxLength={type === 'tel' ? 14 : 320}
      className={[...classes, className || ''].join(' ')}
      style={{
        backgroundImage: bgImage,
        backgroundPositionX: '4%',
        backgroundPositionY: submitted ? '50%' : '65%',
        lineHeight: submitted ? `${INPUT_HEIGHT_PX}px` : undefined,
        borderColor: useDropshadow ? undefined : borderColor,
        // when submitted, mimic legacy padding reset and left icon spacing
        paddingLeft: submitted ? '65px' : undefined,
        paddingTop: submitted ? 0 : undefined,
        paddingBottom: submitted ? 0 : undefined,
        paddingRight: submitted ? 0 : undefined,
        fontSize: '16px',
        ...(style || {})
      }}
    />
  );
});

export default MobilePhoneNumberInput;
